import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import BulletinRichTextEditor from './BulletinRichTextEditor';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { BOARD_MANUAL_PATH } from '../constants/routes';
import { presignAndUploadManualFile, isManualUploadConfiguredError } from '../api/manualUpload';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import {
  DEFAULT_MANUAL_GROUP_NAME,
  type ManualAttachmentPayload,
  type ManualGroupsTreeResponse,
} from '../constants/manual';

const MAX_FILES = 5;

const ManualNew: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const [groups, setGroups] = useState<ManualGroupsTreeResponse['groups']>([]);
  const [groupId, setGroupId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const run = async () => {
      try {
        const res = await apiClient.get<ManualGroupsTreeResponse>('/api/manual/groups');
        setGroups(res.data.groups);
        setGroupId((prev) => {
          if (prev !== '') return prev;
          const uncategorized = res.data.groups.find((g) => g.name === DEFAULT_MANUAL_GROUP_NAME);
          if (uncategorized) return uncategorized.id;
          return res.data.groups.length > 0 ? res.data.groups[0].id : prev;
        });
      } catch {
        setLoadError('グループ一覧の取得に失敗しました');
      }
    };
    void run();
  }, []);

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const next = [...files];
    for (let i = 0; i < list.length; i += 1) {
      if (next.length >= MAX_FILES) break;
      next.push(list[i]);
    }
    setFiles(next);
  };

  const submit = async () => {
    const t = title.trim();
    if (!t) {
      setError('タイトルを入力してください');
      return;
    }
    if (groupId === '' || !Number.isInteger(groupId)) {
      setError('グループを選択してください');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const attachments: ManualAttachmentPayload[] = [];
      for (const f of files) {
        try {
          const meta = await presignAndUploadManualFile(f);
          attachments.push(meta);
        } catch (e: unknown) {
          if (isManualUploadConfiguredError(e)) {
            showSnackbar({
              message: 'ファイル添付は現在利用できません。本文のみ保存する場合は添付を削除してください。',
              severity: 'error',
            });
            setSaving(false);
            return;
          }
          const msg =
            axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
              ? (e.response.data as { error?: string }).error
              : null;
          showSnackbar({ message: msg || 'ファイルのアップロードに失敗しました', severity: 'error' });
          setSaving(false);
          return;
        }
      }

      const res = await apiClient.post<{ id: number }>('/api/manual/manuals', {
        group_id: groupId,
        title: t,
        body,
        attachments,
      });
      showSnackbar({ message: 'マニュアルを作成しました', severity: 'success' });
      navigate(`/manual/${res.data.id}`);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      showSnackbar({ message: msg || 'マニュアルの作成に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        マニュアルを作成
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TextField
          label="タイトル"
          fullWidth
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth required sx={{ mb: 2, maxWidth: 400 }}>
          <InputLabel id="manual-group-label">グループ</InputLabel>
          <Select
            labelId="manual-group-label"
            label="グループ"
            value={groupId === '' ? '' : String(groupId)}
            onChange={(e) => setGroupId(Number(e.target.value))}
          >
            {groups.map((g) => (
              <MenuItem key={g.id} value={String(g.id)}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <BulletinRichTextEditor label="本文" value={body} onChange={setBody} minRows={8} />

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
          添付（最大 {MAX_FILES} 件）
        </Typography>
        <Button variant="outlined" component="label" size="small" sx={{ mb: 1 }} disabled={files.length >= MAX_FILES}>
          ファイルを選択
          <input
            type="file"
            hidden
            multiple
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </Button>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {files.map((f, i) => (
            <Chip
              key={`${f.name}-${i}`}
              label={f.name}
              onDelete={() => setFiles(files.filter((_, j) => j !== i))}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={() => void submit()} disabled={saving}>
            作成する
          </Button>
          <Button variant="text" onClick={() => navigate(BOARD_MANUAL_PATH)} disabled={saving}>
            キャンセル
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ManualNew;
